import { useRef, type KeyboardEvent, type ReactNode, type SVGProps } from "react";

import { t, type StringKey } from "../contract/strings";
import { BoltIcon, BookOpenIcon, RecordVoiceOverIcon, cx } from "../ui";
import { RESULT_TABS, type ResultTab } from "./result-model";

const TAB_LABEL: Record<ResultTab, StringKey> = {
  quick: "result.tab_quick",
  detailed: "result.tab_detailed",
  transcript: "result.transcript",
};

/** A glyph before each label, in the label's colour: at a glance, the whole; per person; who said what. */
const TAB_ICON: Record<ResultTab, (props: SVGProps<SVGSVGElement>) => ReactNode> = {
  quick: BoltIcon,
  detailed: BookOpenIcon,
  transcript: RecordVoiceOverIcon,
};

export function tabId(tab: ResultTab): string {
  return `result-tab-${tab}`;
}

export function panelId(tab: ResultTab): string {
  return `result-panel-${tab}`;
}

/**
 * The segmented control over the notes: Quick, Detailed, Transcript (#764).
 *
 * A WAI-ARIA tab list with a roving tab stop: Tab reaches the open tab only, the arrow
 * keys (and Home/End) move between tabs and open the one they land on.
 */
export function ResultTabBar({
  value,
  onChange,
}: {
  value: ResultTab;
  onChange: (tab: ResultTab) => void;
}) {
  const refs = useRef<Partial<Record<ResultTab, HTMLButtonElement | null>>>({});

  function move(event: KeyboardEvent<HTMLDivElement>): void {
    const at = RESULT_TABS.indexOf(value);
    const last = RESULT_TABS.length - 1;
    const next =
      event.key === "ArrowRight"
        ? (at + 1) % RESULT_TABS.length
        : event.key === "ArrowLeft"
          ? (at - 1 + RESULT_TABS.length) % RESULT_TABS.length
          : event.key === "Home"
            ? 0
            : event.key === "End"
              ? last
              : null;
    if (next === null) return;
    event.preventDefault();
    const tab = RESULT_TABS[next];
    onChange(tab);
    refs.current[tab]?.focus();
  }

  return (
    <div
      role="tablist"
      aria-label={t("result.tabs_aria")}
      onKeyDown={move}
      // Three equal segments; under 360 wide each segment takes its label's width instead,
      // so Transcript fits beside the shorter two on one line down to 320.
      className="flex min-[360px]:grid min-[360px]:grid-cols-3 gap-nudge p-nudge bg-surface-2 border border-line rounded-card"
    >
      {RESULT_TABS.map((tab) => {
        const selected = tab === value;
        const Icon = TAB_ICON[tab];
        return (
          <button
            key={tab}
            ref={(el) => {
              refs.current[tab] = el;
            }}
            type="button"
            role="tab"
            id={tabId(tab)}
            aria-selected={selected}
            // Only the open tab's panel is rendered, so only it is referenced.
            aria-controls={selected ? panelId(tab) : undefined}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(tab)}
            // The selected look is keyed to `aria-selected` itself, so it can only ever sit
            // on the tab the screen reader also hears as selected; hover and focus change
            // the text colour and the focus ring, never the plate.
            // The reference's tab type: mono caps at the count size (`text-count`, 0.7rem),
            // bold, with the name tracking (0.05em) rather than the label register's 0.1em,
            // at every width.
            className={cx(
              "font-code font-bold uppercase tracking-name text-count",
              "flex-auto inline-flex items-center justify-center gap-1 whitespace-nowrap",
              "min-h-icon-button px-1 rounded-control border cursor-pointer transition-colors",
              "bg-transparent border-transparent text-ink-soft hover:text-accent",
              // The open tab is the primary fill, as in the reference: the accent, its label
              // and glyph in the accent ink, a soft shadow. The focus ring (app.css: 2 units
              // of accent, offset 2) sits outside the plate, on the bar's ground.
              "aria-selected:bg-accent aria-selected:border-transparent aria-selected:text-accent-ink",
              "aria-selected:shadow-sm aria-selected:hover:text-accent-ink"
            )}
          >
            {/* The glyph is decoration: the tab's name is its label alone. */}
            <Icon className="size-icon-sm flex-none" aria-hidden="true" />
            {t(TAB_LABEL[tab])}
          </button>
        );
      })}
    </div>
  );
}
